import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import API from '../utils/Axios.utils';

import useAuth from './useAuth';
import { isScorePending } from '../components/common/ResumeScore';

// Helper to get auth token
// const getToken = () => {
//     const user = JSON.parse(localStorage.getItem('userLoggedIn'));
//     return user?.token;
// };

// --- API Functions ---

const fetchCandidates = async ({ page, search, status, jobCode, supplierId, token, selectionStatus, minScore, sortOrder }) => {
    // const token = getToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };

    let url = `/api/profile/supplier_candidate/${supplierId}?page=${page}&pageSize=10`;

    if (search) {
        url += `&search=${search}`;
    }

    if (status) {
        url += `&status=${status}`;
    }

    if (jobCode) {
        url += `&jobCode=${jobCode}`;
    }

    // Resume score filters/sort — handled in SQL by the backend so they apply across
    // the whole result set, not just the page currently loaded
    if (selectionStatus) {
        url += `&selectionStatus=${selectionStatus}`;
    }

    if (minScore) {
        url += `&minScore=${minScore}`;
    }

    if (sortOrder) {
        url += `&sortBy=resume_score&sortOrder=${sortOrder}`;
    }

    const response = await API.get(url, config);
    return response.data;
};

const deleteCandidate = async (profileId, token) => {
    // const token = getToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const response = await API.delete(`/api/profile/${profileId}`, config);
    return response.data;
};

const fetchJobCodes = async (supplierId, token) => {
    // const token = getToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const response = await API.get(`/api/job/jobcode?supplierId=${supplierId || ''}`, config);
    return response.data;
};

const fetchJobSkills = async (jobCode, token) => {
    // const token = getToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const response = await API.get(`/api/job/skills/${jobCode}`, config);
    return response.data;
};

const updateCandidate = async ({ profileId, formData, token }) => {
    // const token = getToken();
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const response = await API.put(`/api/profile/${profileId}`, formData, config);
    return response.data;
};

const importCandidatesFromCSV = async ({ job_code, candidates, token }) => {
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const response = await API.post('/api/profile/import-candidates', { job_code, candidates }, config);
    return response.data;
};

const bulkUploadProfiles = async ({ formData, token }) => {
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const response = await API.post('/api/profile/addbulkprofile', formData, config);
    return response.data;
};

// --- Hooks ---

export const useGetCandidates = ({ page, search, status, jobCode, supplierId, selectionStatus, minScore, sortOrder }, options = {}) => {
    const { token } = useAuth();
    return useQuery({
        // Score filters are part of the key: changing them must refetch, not reuse cache
        queryKey: ['candidates', supplierId, { page, search, status, jobCode, selectionStatus, minScore, sortOrder }],
        queryFn: () => fetchCandidates({ page, search, status, jobCode, supplierId, token, selectionStatus, minScore, sortOrder }),
        placeholderData: (previousData) => previousData, // Keep previous data while fetching new data to prevent flicker
        staleTime: 60 * 1000, // 1 minute
        enabled: !!supplierId,
        // Scores land asynchronously after submit: poll while any just-added row is still unscored
        refetchInterval: (query) =>
            query.state.data?.profile?.some(isScorePending) ? 10 * 1000 : false,
        ...options,
    });
};

export const useDeleteCandidate = () => {
    const queryClient = useQueryClient();
    const { token } = useAuth();

    return useMutation({
        mutationFn: (profileId) => deleteCandidate(profileId, token),
        onSuccess: () => {
            // Invalidate candidates list to trigger refetch
            queryClient.invalidateQueries({ queryKey: ['candidates'] });
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
        },
    });
};

export const useGetJobCodes = (supplierId) => {
    // Staff see every job code; suppliers only their own.
    const { token, isStaff } = useAuth();
    return useQuery({
        queryKey: ['jobCodes', supplierId],
        queryFn: () => fetchJobCodes(supplierId, token),
        enabled: !!supplierId || isStaff,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

export const useGetJobSkills = (jobCode) => {
    const { token } = useAuth();
    return useQuery({
        queryKey: ['jobSkills', jobCode],
        queryFn: () => fetchJobSkills(jobCode, token),
        enabled: !!jobCode,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

export const useUpdateCandidate = () => {
    const queryClient = useQueryClient();
    const { token } = useAuth();

    return useMutation({
        mutationFn: (variables) => updateCandidate({ ...variables, token }),
        onSuccess: (data, variables) => {
            // Invalidate specific candidate queries and the list
            queryClient.invalidateQueries({ queryKey: ['candidates'] });
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
            // Also could invalidate detailed view if we had a query for that
        },
    });
};

export const useImportCandidatesFromCSV = () => {
    const queryClient = useQueryClient();
    const { token } = useAuth();
    return useMutation({
        mutationFn: (variables) => importCandidatesFromCSV({ ...variables, token }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['candidates'] });
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
        },
    });
};

export const useBulkUploadProfiles = () => {
    const queryClient = useQueryClient();
    const { token } = useAuth();
    return useMutation({
        mutationFn: (variables) => bulkUploadProfiles({ ...variables, token }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['candidates'] });
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
        },
    });
};

export const useAutofillFromResume = () => {
    const { token } = useAuth();
    return useMutation({
        mutationFn: async (file) => {
            const formData = new FormData();
            formData.append('resume', file);
            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            };
            const response = await API.post('/api/profile/autofill', formData, config);
            return response.data;
        },
    });
};
